import {
  ANDROID_LINK,
  APP_STORE_LINK,
  COMPLETE_HEALTH_ASSESSMENT,
  COMPLETE_PRE_JOURNEY_TASKS,
  DAY_FIVE_CHECK_IN,
  DAY_FOUR_CHECK_IN,
  DAY_ONE_CHECK_IN,
  DAY_THREE_CHECK_IN,
  DAY_TWO_CHECK_IN,
  FIRST_WEEK_CELEBRATION,
  HEALTH_ASSESSMENT_TASK_DETAILS,
  JOURNEY_KICK_OFF_CALL,
  JOURNEY_KICK_OFF_CALL_TASK,
  SHARE_OPTAVIA_APP,
  WEEKLY_CHECK_IN,
  WELCOME_MESSAGE_SHOPPING_LIST,
} from '/enum/clientJourneyTasks.enum';
import {WEEKLY_CHECK_INS} from '/enum/clientJourneyTasks.enum';
import {EVENT_NAMES} from '/enum/tealium.enum';
import {copyToClipboard} from '/util/clipboard.util';
import {generateCallback} from '/util/component.util';
import {getDateUtilDefaultInstance} from '/util/date.util';
import {getTealiumUtilDefaultInstance} from '/util/tealium';

/**
 * @param {Object[]} item
 * @param {string}   item.chkBoxCode
 * @param {string}   item.checkStatus
 *
 * @param {Object[]} initialTasks
 * @param {string}   initialTasks.tabDescription
 * @param {string}   initialTasks.screenTabCode
 * @param {Object[]} initialTasks.checkBox
 * @param {string}   initialTasks.checkBox.screenTabCode
 * @param {string}   initialTasks.checkBox.chkBoxCode
 * @param {string}   initialTasks.checkBox.description
 */
export const getTaskLabel = (item: any, initialTasks: any) => {
  return initialTasks
    ?.map(({checkBox}: any) => checkBox)
    .flat()
    .find((task: any) => task?.chkBoxCode === item?.chkBoxCode)?.description;
};

/**
 * @param {Object[]} tasks
 * @param {number}   tasks.clientId
 * @param {string}   tasks.screenTabCode
 * @param {Object[]} tasks.checkIns
 * @param {string}   tasks.checkIns.chkBoxCode
 * @param {boolean}  tasks.checkIns.checkStatus
 *
 * @param {Object[]} initialTasks
 * @param {string}   initialTasks.tabDescription
 * @param {string}   initialTasks.screenTabCode
 * @param {Object[]} initialTasks.checkBox
 * @param {string}   initialTasks.checkBox.screenTabCode
 * @param {string}   initialTasks.checkBox.chkBoxCode
 * @param {string}   initialTasks.checkBox.description
 */

export const getTasksWithLabel = (tasks: any, initialTasks: any) => {
  return tasks
    ?.map(({checkIns}: any) => checkIns)
    .flat()
    .map((item: any, index: any) => {
      return {
        ...item,
        id: index + 1,
        label: getTaskLabel(item, initialTasks),
      };
    });
};

/*  this function is called when there's no response from the api.
 it displays the  tab component with the default tasks as false */

export const getNewTasks = (clientId: any, initialTasks: any) => {
  return Array.from({length: initialTasks.length}).map((item, i) => {
    return {
      // @ts-expect-error TS(2698): Spread types may only be created from object types... Remove this comment to see the full error message
      ...item,
      checkIns: [
        {
          chkBoxCode: initialTasks.map(({checkBox}: any) => checkBox).flat()[i]
            ?.chkBoxCode,
          checkStatus: false,
        },
      ],
      clientId: clientId,
      screenTabCode: initialTasks.find((task: any) => task)?.screenTabCode,
    };
  });
};

/**
 * @param {object[]}  tasks
 * @param {string}  tasks.chkBoxCode
 * @param {boolean} tasks.checkStatus
 * @param {number}  tasks.id
 * @param {string}  tasks.label
 *
 * @returns {{checkStatus: string, chkBoxCode: string}}
 */

export const getTaskDataToSave = (tasks: any) => {
  return tasks?.reduce(
    (acc: any, item: any) =>
      acc.concat({
        chkBoxCode: item.chkBoxCode,
        checkStatus: item.checkStatus,
      }),
    [],
  );
};

/**
 * @param {object}  tasks
 * @param {string}  tasks.chkBoxCode
 * @param {boolean} tasks.checkStatus
 * @param {number}  tasks.id
 * @param {string}  tasks.label
 *
 * @param {Object[]} initialTasks
 * @param {string}   type
 * @param {number}   initialTasks.clientId
 * @param {string}   initialTasks.screenTabCode
 * @param {Object[]} initialTasks.checkIns
 * @param {string}   initialTasks.checkIns.chkBoxCode
 * @param {boolean}  initialTasks.checkIns.checkStatus
 */
export const getUpdatedTasks = (
  tasks: any,
  initialTasks: any,
  type: any,
  lastCheckIn: any,
) => {
  const currentTime = getDateUtilDefaultInstance().getNow();
  const newTasks = getTaskDataToSave(tasks);

  return initialTasks?.map((initialTask: any) => {
    // Create a new checkIns array with updated checkStatus
    const updatedCheckIns = initialTask.checkIns.map((item: any) => {
      const task = newTasks.find((x: any) => x.chkBoxCode === item.chkBoxCode);
      return {
        ...item,
        checkStatus: task?.checkStatus ?? item.checkStatus,
      };
    });

    // Determine updated check-in date if applicable
    let currentCheckInDate = initialTask.currentCheckInDate;

    if (type === WEEKLY_CHECK_INS) {
      currentCheckInDate =
        updatedCheckIns[0]?.checkStatus === true
          ? currentTime
          : lastCheckIn ?? null;
    }

    // Return a new object to avoid mutation
    return {
      ...initialTask,
      checkIns: updatedCheckIns,
      currentCheckInDate,
    };
  });
};

/**
 * @param {Object[]} tasks
 * @param {number}   tasks.clientId
 * @param {string}   tasks.screenTabCode
 * @param {Object[]} tasks.checkIns
 * @param {string}   tasks.checkIns.chkBoxCode
 * @param {boolean}  tasks.checkIns.checkStatus
 *
 * @param {Object}   data
 * @param type
 * @param {string}   data.chkBoxCode
 * @param {boolean}  data.checkStatus
 */
export const getTaskDataWithMeta = (tasks: any, data: any, type: any) => {
  const currentTime = getDateUtilDefaultInstance().getNow();
  const currentDate = Math.round(
    getDateUtilDefaultInstance().getEpoch(currentTime) * 1000,
  );
  const taskMeta = tasks.map(({clientId, screenTabCode}: any) =>
    type === WEEKLY_CHECK_INS
      ? {
          clientId,
          checkInDateTime: currentDate,
          screenTabCode,
          createdSystem: 'CONNAPP',
        }
      : {
          clientId,
          screenTabCode,
          createdSystem: 'CONNAPP',
        },
  )[0];

  return Object.assign({}, taskMeta, {
    checkIns: data,
  });
};

/**
 * @param {object}  tasks
 * @param {string}  tasks.chkBoxCode
 * @param {boolean} tasks.checkStatus
 * @param {number}  tasks.id
 * @param {string}  tasks.label
 * @param {number}  id
 *
 * @returns {tasks}
 */
export const getTasksOnChange = (tasks: any, id: any, status: any) => {
  return tasks?.map((task: any) =>
    task.id === id ? {...task, checkStatus: status} : task,
  );
};

export const getFormattedString = (str: any) => {
  return str
    ?.toLowerCase()
    .replace(/[^a-zA-Z0-9]+(.)/g, (m: any, chr: any) => chr.toUpperCase());
};

// @ts-expect-error TS(7030): Not all code paths return a value.
export const getTaskNameForTealium = (label: any) => {
  switch (getFormattedString(label)) {
    case getFormattedString(COMPLETE_PRE_JOURNEY_TASKS):
      return 'complete_pre_journey_tasks';

    case getFormattedString(JOURNEY_KICK_OFF_CALL):
    case getFormattedString(JOURNEY_KICK_OFF_CALL_TASK):
      return 'journey_kickoff_call';

    case getFormattedString(DAY_ONE_CHECK_IN):
      return 'checkin_day1';

    case getFormattedString(DAY_TWO_CHECK_IN):
      return 'checkin_day2';

    case getFormattedString(DAY_THREE_CHECK_IN):
      return 'checkin_day3';

    case getFormattedString(DAY_FOUR_CHECK_IN):
      return 'checkin_day4';

    case getFormattedString(DAY_FIVE_CHECK_IN):
      return 'checkin_day5';

    case getFormattedString(FIRST_WEEK_CELEBRATION):
      return 'first_week_celebrate';

    case getFormattedString(COMPLETE_HEALTH_ASSESSMENT):
      return 'health_assessment';

    case getFormattedString(WELCOME_MESSAGE_SHOPPING_LIST):
      return 'shopping_list';

    case getFormattedString(SHARE_OPTAVIA_APP):
      return 'app_share';

    case getFormattedString(WEEKLY_CHECK_IN):
      return 'weekly_checkin';
  }
};

/**
 * Generates a callback to be called when the people to contact is pressed
 *
 * @param {object} params - Parameters
 * @param {string} params.filterType  - current people to contact filter value
 * @param {function} params.currentScreen -current screen name
 *
 * @returns {function} Callback - A function to be called when tab is pressed
 */

export const generateOnCheckboxChange = ({
  checkboxName,
  currentScreenName,
  checkedStatus,
  lastPaidRank,
}: any) => {
  const sendEvent = getTealiumUtilDefaultInstance().generateTrackEvent({
    eventName: EVENT_NAMES.checkboxClick,
  });

  const dateUtil = getDateUtilDefaultInstance();
  const checkedDate = dateUtil.formatDate(new Date(), 'MM_dd_yyyy');

  sendEvent({
    checkboxName: getTaskNameForTealium(checkboxName),
    checkedDate,
    currentScreenName,
    checkedStatus,
    lastPaidRank,
  });
};

export const isTabCompleted = (tasks: any) => {
  if (tasks?.length === 0) {
    return false;
  } else {
    return tasks
      ?.map(({checkIns}: any) => checkIns)
      .flat()
      .every(({checkStatus}: any) => checkStatus === true);
  }
};

export const appStoreLink =
  (external_link: any, lastPaidRank: any) => async () => {
    await new Promise(resolve => {
      const sendEvent = getTealiumUtilDefaultInstance().generateTrackEvent({
        eventName: EVENT_NAMES.navigationClick,
      });
      resolve(
        sendEvent({
          buttonName: 'share_app',
          buttonType: external_link,
          lastPaidRank: lastPaidRank,
        }),
      );
    }).then(
      generateCallback({
        func: copyToClipboard,
        params: APP_STORE_LINK,
      }),
    );
  };

export const getFormattedWeeklyCheckInsDate = (weeklyCheckInsDate: any) => {
  if (weeklyCheckInsDate) {
    return getDateUtilDefaultInstance().formatDate(
      new Date(weeklyCheckInsDate),
      'MMMM d, yyyy',
    );
  } else {
    return null;
  }
};

export const androidLink =
  (external_link: any, lastPaidRank: any) => async () => {
    await new Promise(resolve => {
      const sendEvent = getTealiumUtilDefaultInstance().generateTrackEvent({
        eventName: EVENT_NAMES.navigationClick,
      });
      resolve(
        sendEvent({
          buttonName: 'share_app',
          buttonType: external_link,
          lastPaidRank: lastPaidRank,
        }),
      );
    }).then(
      generateCallback({
        func: copyToClipboard,
        params: ANDROID_LINK,
      }),
    );
  };

export const generateCopyLink =
  (external_link: any, lastPaidRank: any) => async () => {
    await new Promise(resolve => {
      const sendEvent = getTealiumUtilDefaultInstance().generateTrackEvent({
        eventName: EVENT_NAMES.navigationClick,
      });
      resolve(
        sendEvent({
          buttonName: 'view_health_online',
          buttonType: external_link,
          lastPaidRank: lastPaidRank,
        }),
      );
    }).then(
      generateCallback({
        func: copyToClipboard,
        params: HEALTH_ASSESSMENT_TASK_DETAILS,
      }),
    );
  };
